<template>
  <div class="modal__BuilderBlockWrapper">
    <div v-for="(content, index) in contents" :key="index">
      <!-- Image field must have grandchildIndex with value image -->
      <p v-if="index === 'image'">
        <image-upload :required="true" v-model="contents[index]"></image-upload>
      </p>
      <div v-else-if="index === 'authors'" :key="index">
        <search-employee :lang="lang" :employees="contents[index]" v-model="contents[index]" />
      </div>
      <p v-else-if="!['repeat', 'type'].includes(index) && typeof content === 'string'">
        <label :for="`website-label-${index}`" class="formLabel">{{ capitalize(index).replace(/_/g, ' ') }}</label>
        <input type="text" class="formInput" :id="`website-string-text-${index}`" v-model="contents[index]" />
      </p>
      <p v-else-if="typeof content === 'object' && content.type">
        <label :for="`website-label-${index}`" class="formLabel">{{ capitalize(index).replace(/_/g, ' ') }}</label>
        <template v-if="content.type === 'editor'">
          <text-editor v-model="content.value" />
        </template>
        <template v-else-if="content.type === 'select'">
          <select class="formSelect" v-model="content.value">
            <option :value="''">None</option>
            <option :value="option" v-for="option in content.values" :key="option">
              {{ option }}
            </option>
          </select>
        </template>
        <template v-else-if="content.type === 'textarea'">
          <textarea class="formInput--Textarea" :id="`website-field-${content}`" v-model="content.value"></textarea>
        </template>
      </p>

      <!-- Grid object (basically a flexible content field) -->
      <div v-else-if="index === 'grid'">
        <div
          class="modal__BuilderBlockWrapper--Repeater"
          v-for="(column, childIndex) in content.columns"
          :key="column.id"
        >
          <ul class="modal__BuilderOptions">
            <li class="modal__BuilderOptionsItem--Title">Column</li>
            <li class="modal__BuilderOptionsItem--Delete">
              <button class="modal__BuilderButton" @click="removeRepeater(index, childIndex, content.columns)">
                <icon name="trash-alt"></icon>
                <span>Remove repeater</span>
              </button>
            </li>
            <template v-if="content.columns.length > 1">
              <li class="modal__BuilderOptionsItem">
                <button
                  class="modal__BuilderButton"
                  @click="moveRepeater(index, childIndex, childIndex - 1, content.columns)"
                >
                  <icon name="arrow-up"></icon>
                  <span>One up</span>
                </button>
              </li>
              <li class="modal__BuilderOptionsItem">
                <button
                  class="modal__BuilderButton"
                  @click="moveRepeater(index, childIndex, childIndex + 1, content.columns)"
                >
                  <icon name="arrow-down"></icon>
                  <span>One down</span>
                </button>
              </li>
            </template>
          </ul>
          <website-block v-model="column.contents" :contents="column.contents" :lang="lang" />
        </div>

        <nav class="modal__Add--Repeater">
          <span class="modal__AddIcon">
            <icon name="plus" weight="regular"></icon>
          </span>

          <ul class="modal__AddList--Repeater">
            <li v-for="block in content.blocks" :key="block.id">
              <button @click="addColumn(block, content.columns)">
                {{ capitalize(block.name).replace(/_/g, ' ') }}
              </button>
            </li>
          </ul>
        </nav>
      </div>

      <!-- Repeater field -->
      <div v-else-if="typeof content === 'object' && Array.isArray(content)">
        <div
          class="modal__BuilderBlockWrapper--Repeater"
          v-for="(child, childIndex) in contents[index]"
          :key="child.id"
        >
          <ul class="modal__BuilderOptions">
            <li class="modal__BuilderOptionsItem--Title">Block</li>
            <template v-if="contents[index].length > 1">
              <li class="modal__BuilderOptionsItem--Delete">
                <button class="modal__BuilderButton" @click="removeRepeater(index, childIndex)">
                  <icon name="trash-alt"></icon>
                  <span>Remove repeater</span>
                </button>
              </li>
              <li class="modal__BuilderOptionsItem">
                <button class="modal__BuilderButton" @click="moveRepeater(index, childIndex, childIndex - 1)">
                  <icon name="arrow-up"></icon>
                  <span>One up</span>
                </button>
              </li>
              <li class="modal__BuilderOptionsItem">
                <button class="modal__BuilderButton" @click="moveRepeater(index, childIndex, childIndex + 1)">
                  <icon name="arrow-down"></icon>
                  <span>One down</span>
                </button>
              </li>
            </template>
          </ul>
          <website-block v-model="contents[index][childIndex]" :contents="contents[index][childIndex]" :lang="lang" />
        </div>
        <p>
          <button class="button" v-if="contents[index][0] && contents[index][0].repeat" @click="addRepeater(index)">
            Add block
          </button>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import Capitalize from '@/components/mixins/Capitalize.js'

import ImageUpload from '@/components/editor/ImageUpload.vue'
import SearchEmployee from '@/components/editor/blocks/SearchEmployee.vue'
import WebsiteBlock from '@/components/editor/blocks/WebsiteBlock.vue'
import TextEditor from '@/components/editor/TextEditor.vue'

export default {
  name: 'WebsiteBlock',
  props: ['contents', 'lang'],
  mixins: [Capitalize],
  components: { ImageUpload, SearchEmployee, WebsiteBlock, TextEditor },
  watch: {
    block: {
      deep: true,
      handler() {
        this.$emit('input', this.block)
      },
    },
  },
  data() {
    return {
      repeaterDefaults: {},
    }
  },
  methods: {
    addColumn(block, columns) {
      columns.push(JSON.parse(JSON.stringify(block)))
    },
    addRepeater(index) {
      this.contents[index].push(JSON.parse(JSON.stringify(this.repeaterDefaults[index][0])))
    },
    removeRepeater(index, childIndex, obj = this.contents[index]) {
      obj.splice(childIndex, 1)
    },
    moveRepeater(index, indexA, indexB, obj = this.contents[index]) {
      obj.splice(indexB, 0, obj.splice(indexA, 1)[0])
    },
  },
  mounted() {
    // Store initial contents value for each repeater field (aka every field that's an array) as an indexed array
    Object.keys(this.contents).forEach((key) => {
      if (Array.isArray(this.contents[key])) this.repeaterDefaults[key] = [{ ...this.contents[key][0] }]
    })
  },
}
</script>
